// frontend/src/auth/auth.js

import { apiClient } from '../api/client';
import { createContext } from 'react';

// Create the auth context with default values
export const authContext = createContext({
  user: null,
  setUser: () => {},
  isAuthenticated: false,
  isAdmin: false,
  logout: () => {}
});

// Store the JWT token
export const setToken = (token) => {
  localStorage.setItem('token', token);
};

// Get the JWT token
export const getToken = () => {
  return localStorage.getItem('token');
};

// Remove the JWT token
export const removeToken = () => {
  localStorage.removeItem('token');
};

// Store user data
export const setUser = (user) => {
  localStorage.setItem('user', JSON.stringify(user));
};

// Get user data
export const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

// Remove user data
export const removeUser = () => {
  localStorage.removeItem('user');
};

// Login function with retry logic
export const login = async (email, password, retryCount = 0) => {
  try {
    console.log('Attempting login with email:', email);
    const response = await apiClient.post('/auth/login', { email, password });
    console.log('Login response:', response);
    
    if (response.data.success) {
      const { token, user } = response.data.data;
      console.log('Received token:', token ? 'Token exists' : 'No token received');
      console.log('Received user:', user);
      setToken(token);
      setUser(user);
      
      // Verify token was stored
      const storedToken = getToken();
      console.log('Stored token verification:', storedToken ? 'Token stored successfully' : 'Token not stored');
      
      return { success: true, user };
    } else {
      console.log('Login unsuccessful:', response.data.message);
      return { 
        success: false, 
        message: response.data.message || 'Login failed. Please try again.' 
      };
    }
  } catch (error) {
    console.error('Login error:', error);
    
    // If we haven't retried too many times and it's a timeout error, retry
    if (retryCount < 2 && error.message?.includes('timed out')) {
      console.log(`Retrying login attempt ${retryCount + 1}...`);
      return login(email, password, retryCount + 1);
    }
    
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'An error occurred during login. Please try again.' 
    };
  }
};

// Register function
export const register = async (userData) => {
  try {
    const response = await apiClient.post('/auth/register', userData);
    
    if (response.data.success) {
      return { 
        success: true, 
        message: response.data.message || 'Registration successful' 
      };
    } else {
      return { 
        success: false, 
        message: response.data.message || 'Registration failed' 
      };
    }
  } catch (error) {
    console.error('Registration error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'An error occurred during registration' 
    };
  }
};

// Logout function
export const logout = () => {
  removeToken();
  removeUser();
};

// Check if token is valid
export const validateToken = async () => {
  try {
    const token = getToken();
    if (!token) return { valid: false };
    
    const response = await apiClient.get('/auth/validate-token');
    return response.data;
  } catch (error) {
    console.error('Token validation error:', error);
    return { valid: false };
  }
};

// Auth context (can be used with useAuth hook)
export const useAuth = () => {
  const user = getUser();
  return {
    user,
    setUser: (newUser) => {
      if (newUser) {
        setUser(newUser);
      } else {
        removeUser();
      }
    },
    isAuthenticated: !!user,
    isAdmin: user ? user.isAdmin : false,
    logout
  };
};